import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ColorTheme, TextTheme } from '../../globaStyledComponents/globalStyledComponentsStyling';

const VerificationContainer = styled.div<{ invalid?: boolean }>`
    display: flex;
    gap: 8px;
    justify-content: center;
    margin: 0 auto 0;
    border: ${props => props.invalid ? `1px solid ${ColorTheme.Red1.color}` : 'none'};
    border-radius: 12px;
    padding: 8px;
`;

const VerificationInput = styled.input`
    width: 44px;
    height: 54px;
    border: 1px solid ${ColorTheme.Grey1.color};
    border-radius: 8px;
    text-align: center;
    font-size: ${TextTheme.sans_600_semi.fontSize};
    font-family: ${TextTheme.sans_600_semi.fontFamily};
    line-height: ${TextTheme.sans_600_semi.lineHeight};
    outline: none;
    padding: 0;
    margin: 0;

    &:focus {
        border-color: ${ColorTheme.Black.color};
    }
`;

interface CustomVerificationInputProps {
    length?: number;
    value: string;
    onChange: (value: string) => void;
  onComplete: (value: string) => void;
  invalid?: boolean;
  onBlur?: () => void;
}

const CustomVerificationInput: React.FC<CustomVerificationInputProps> = ({
    length = 6,
    value,
    onChange,
    onComplete,
    invalid,
    onBlur
}) => {
    const [inputs, setInputs] = useState<string[]>(Array(length).fill(''));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        // Initialize refs array
        inputRefs.current = inputRefs.current.slice(0, length);
    }, [length]);

    useEffect(() => {
        // Update local state when value prop changes
        if (value) {
            setInputs(value.split('').concat(Array(length - value.length).fill('')));
        }
    }, [value, length]);

    const handleInputChange = (index: number, inputValue: string) => {
        // Only allow numbers
        if (!/^\d*$/.test(inputValue)) return;

        const newInputs = [...inputs];
        newInputs[index] = inputValue;
        setInputs(newInputs);

        const newValue = newInputs.join('');
        onChange(newValue);

        // Move to next input if current input is filled
        if (inputValue && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }

        // Check if all inputs are filled
        if (newValue.length === length) {
            onComplete(newValue);
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !inputs[index] && index > 0) {
            // Move to previous input on backspace if current input is empty
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, length);
        if (!/^\d*$/.test(pastedData)) return;

        const newInputs = pastedData.split('').concat(Array(length - pastedData.length).fill(''));
        setInputs(newInputs);
        onChange(pastedData);

        if (pastedData.length === length) {
            onComplete(pastedData);
        }
    };

    return (
        <VerificationContainer invalid={invalid}>
            {Array(length).fill(0).map((_, index) => (
                <VerificationInput
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={inputs[index]}
                    onChange={e => handleInputChange(index, e.target.value)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    onBlur={onBlur}
                />
            ))}
        </VerificationContainer>
    );
};

export default CustomVerificationInput; 